import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import SequenceConfigPanel from '../components/SequenceConfigPanel';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function AccountDetail() {
  const { getToken } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', plan_track: '', plan_tier: '', billing_email: '', status: '' });

  async function load() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API}/accounts/${id}`, { headers: { 'Authorization': 'Bearer ' + token } });
      const d = await r.json();
      setData(d);
      if (d.account) setForm({
        name: d.account.name || '',
        plan_track: d.account.plan_track || '',
        plan_tier: d.account.plan_tier || '',
        billing_email: d.account.billing_email || '',
        status: d.account.status || 'active',
      });
    } catch (e) { console.error(e); }
    setLoading(false);
  }
  useEffect(() => { load(); }, [id]);

  async function handleSave() {
    try {
      await fetch(`${API}/accounts/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (await getToken()) },
        body: JSON.stringify(form),
      });
      setEditing(false);
      await load();
    } catch (e) { console.error(e); }
  }

  async function handleDelete() {
    if (!data?.account) return;
    const name = data.account.name;
    const workspaceCount = data.workspaces?.length || 0;
    const msg = workspaceCount > 0
      ? `Delete ${name} AND its ${workspaceCount} workspace${workspaceCount === 1 ? '' : 's'}? This permanently removes all clients, contacts, sequences, and activity under this account. This CANNOT be undone.`
      : `Delete ${name}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    if (!window.confirm(`Confirm: hard delete ${name}? Type-equivalent confirmation.`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API}/accounts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (!res.ok) {
        const body = await res.text();
        alert(`Delete failed (${res.status}): ${body}`);
        return;
      }
      navigate('/admin/accounts');
    } catch (e) { alert('Delete failed: ' + e.message); }
  }

  if (loading) return <div style={{ padding: '2rem' }}><div style={{ height: '60px', background: '#EEECEA', borderRadius: '10px' }} /></div>;
  if (!data || !data.account) return <div style={{ padding: '2rem', color: '#888' }}>Account not found</div>;

  const { account, workspaces, users, plan } = data;
  const tierOptions = form.plan_track === 'managed_service'
    ? [{ v: 'launchpad', l: 'Launchpad' }, { v: 'growth', l: 'Growth' }, { v: 'partner', l: 'Partner' }]
    : form.plan_track === 'saas'
    ? [{ v: 'starter', l: 'Starter' }, { v: 'growth', l: 'Growth' }, { v: 'agency', l: 'Agency' }]
    : [];

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto', boxSizing: 'border-box' }}>
      <button onClick={() => navigate('/admin/accounts')}
        style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}>
        ← All accounts
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '500', margin: '0 0 0.3rem', color: '#1A1A1A' }}>{account.name}</h1>
          <p style={{ margin: 0, fontSize: '0.83rem', color: '#888' }}>
            {account.plan_track === 'internal' ? 'Internal' : (plan?.name || account.plan_tier)} · {account.status}
            {plan?.label && ` · ${plan.label}`}
          </p>
        </div>
        <button onClick={() => setEditing(!editing)}
          style={{ background: editing ? '#FFF' : '#1A1A1A', color: editing ? '#4A5568' : '#FFF', border: editing ? '1px solid #D4CFC9' : 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }}>
          {editing ? 'Cancel' : 'Edit account'}
        </button>
      </div>

      {editing && (
        <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem', marginBottom: '2rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Name</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Billing email</label>
              <input value={form.billing_email} onChange={e => setForm({...form, billing_email: e.target.value})}
                style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Plan</label>
              <select value={form.plan_track} onChange={e => setForm({...form, plan_track: e.target.value, plan_tier: e.target.value === 'managed_service' ? 'partner' : e.target.value === 'saas' ? 'starter' : ''})}
                style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }}>
                <option value="managed_service">Managed</option>
                <option value="saas">SaaS</option>
                <option value="internal">Internal</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Tier</label>
              <select value={form.plan_tier} onChange={e => setForm({...form, plan_tier: e.target.value})} disabled={form.plan_track === 'internal'}
                style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }}>
                {tierOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                {form.plan_track === 'internal' && <option value="">—</option>}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={handleSave}
              style={{ background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }}>
              Save changes
            </button>
            <button onClick={handleDelete}
              style={{ background: 'none', color: '#C53030', border: '1px solid #FEB2B2', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }}>
              Delete account
            </button>
          </div>
        </div>
      )}

      <h2 style={{ fontSize: '1rem', fontWeight: '500', margin: '0 0 0.75rem', color: '#1A1A1A' }}>Workspaces ({workspaces.length})</h2>
      <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden', marginBottom: '2rem' }}>
        {workspaces.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: '#888', fontSize: '0.85rem' }}>No workspaces yet</div>
        ) : workspaces.map((w, i) => (
          <div key={w.id} onClick={() => navigate(`/clients/${w.id}`)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.85rem 1.25rem', borderTop: i > 0 ? '0.5px solid #F0EDE9' : 'none', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = '#FAF8F6'}
            onMouseLeave={e => e.currentTarget.style.background = '#FFF'}>
            <div>
              <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A' }}>{w.name}</p>
              <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: '#888' }}>{w.sector || '—'} · {w.status}</p>
            </div>
            <p style={{ margin: 0, fontSize: '0.78rem', color: '#888' }}>
              {(w.credits_remaining || 0) + (w.credits_topup || 0)} credits
            </p>
          </div>
        ))}
      </div>

      <SequenceConfigPanel level="account" id={id} />

      <h2 style={{ fontSize: '1rem', fontWeight: '500', margin: '0 0 0.75rem', color: '#1A1A1A' }}>Users ({users.length})</h2>
      <div style={{ background: '#FFF', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
        {users.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: '#888', fontSize: '0.85rem' }}>No users yet</div>
        ) : users.map((u, i) => (
          <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.85rem 1.25rem', borderTop: i > 0 ? '0.5px solid #F0EDE9' : 'none', opacity: u.deactivated_at ? 0.55 : 1 }}>
            <div>
              <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A' }}>{u.name || u.email}</p>
              <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: '#888' }}>{u.email} · {u.workspace_name} · {u.role}{u.deactivated_at ? ' · Deactivated' : ''}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
